import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../store";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import { fetchUsers } from "../store/features/userSlice";
import { fetchCities } from "../store/features/citySlice";
import { fetchAllTrucks, selectAllTrucks } from "../store/features/allTrucksSlice";
import DetailPageHeader from "../components/common/DetailPageHeader";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import StandbyOrderForm, {
  STANDBY_ORDER_FORM_ID,
} from "../components/standbyOrders/StandbyOrderForm";
import {
  buildStandbyCreatePayload,
  buildStandbyUpdatePayload,
  emptyStandbyOrderFormValues,
  getStandbyAdditionalExpenses,
  standbyOrderToFormValues,
  type StandbyOrder,
  type StandbyOrderFormValues,
} from "../types/standbyOrder";
import { isOrderEditable, OrderStatus, type OrderExpenseLine } from "../types/order";
import { canMutateOrders, isFinance, isUserAdmin } from "../types/user";
import {
  createStandbyOrderApi,
  getStandbyOrderByIdApi,
  updateStandbyOrderApi,
} from "../api/standbyOrdersApi";
import { getErrorMessage } from "../utils/errorHandling";
import { getRevisionExpenseReviewKeys } from "../utils/embeddedExpenseReview";
import { getOrderWorkflowStepKey, ORDER_WORKFLOW_STEP_KEYS } from "../utils/orderStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import { TestId } from "../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

const formatCreationTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const StandbyOrderFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user, users } = useSelector((state: RootState) => state.user);
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);
  const { cities } = useSelector((state: RootState) => state.cities);
  const trucks = useSelector(selectAllTrucks);

  const canSelectTeam = isUserAdmin(user);
  const memberTeams = useMemo(() => {
    if (!user || canSelectTeam) {
      return operationsTeams;
    }
    return operationsTeams.filter((team) => team.userIds.includes(user._id));
  }, [operationsTeams, user, canSelectTeam]);

  const defaultTeamId = useMemo(() => {
    if (isEdit || canSelectTeam || memberTeams.length === 0) {
      return "";
    }
    return memberTeams[0]._id;
  }, [canSelectTeam, isEdit, memberTeams]);

  const [initialValues, setInitialValues] = useState<StandbyOrderFormValues>(() => ({
    ...emptyStandbyOrderFormValues(),
    teamOperationsId: defaultTeamId,
  }));
  const [loadedOrder, setLoadedOrder] = useState<StandbyOrder | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
    dispatch(fetchUsers());
    dispatch(fetchCities());
    dispatch(fetchAllTrucks());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit || canSelectTeam || memberTeams.length === 0) {
      return;
    }
    setInitialValues((prev) => ({ ...prev, teamOperationsId: memberTeams[0]._id }));
  }, [canSelectTeam, isEdit, memberTeams]);

  useEffect(() => {
    if (!isEdit || !id) {
      return;
    }
    const loadOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const order = await getStandbyOrderByIdApi(id);
        setLoadedOrder(order);
        if (isOrderEditable(order.status)) {
          setInitialValues(standbyOrderToFormValues(order));
        }
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchStandbyOrder"));
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [isEdit, id, t]);

  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isFinance(user) || !canMutateOrders(user)) {
    return <Navigate to="/orders" replace />;
  }

  if (isEdit && loadedOrder && !isOrderEditable(loadedOrder.status)) {
    return <Navigate to={`/orders/standby/${id}`} replace />;
  }

  const additionalExpenseIds = loadedOrder
    ? getStandbyAdditionalExpenses(loadedOrder.expenses)
        .map((expense) => expense.id)
        .filter((expenseId): expenseId is string => Boolean(expenseId))
    : [];

  const handleSubmit = async (values: StandbyOrderFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        await updateStandbyOrderApi(
          id,
          buildStandbyUpdatePayload(values, { additionalExpenseIds }),
        );
        toastEmitter.emit(t("standby.updatedSuccessfully" as TranslationKeys), "success");
        navigate(`/orders/standby/${id}`);
      } else {
        const teamOperationsId = canSelectTeam
          ? values.teamOperationsId
          : values.teamOperationsId || defaultTeamId;
        const order = await createStandbyOrderApi(
          buildStandbyCreatePayload({ ...values, teamOperationsId }),
        );
        toastEmitter.emit(t("standby.createdSuccessfully" as TranslationKeys), "success");
        navigate(`/orders/standby/${order.readableId}`);
      }
    } catch (err: any) {
      const errorMessage = getErrorMessage(
        err,
        t,
        isEdit ? "updateStandbyOrder" : "createStandbyOrder",
      );
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const histories = loadedOrder?.histories ?? [];
  const oldest = histories.length
    ? [...histories].sort((a, b) => a.timestamp - b.timestamp)[0]
    : null;
  const formMeta = isEdit
    ? {
        createdAtLabel: oldest ? formatCreationTimestamp(oldest.timestamp) : undefined,
        createdByLabel: oldest?.userName || loadedOrder?.creator?.name || oldest?.userId,
        reportReadableId: loadedOrder?.uangJalanReportReadableId,
        reportUrl: loadedOrder?.uangJalanReportReadableId
          ? `/uang-jalan-reports/${loadedOrder.uangJalanReportReadableId}`
          : undefined,
      }
    : undefined;

  const revisionExpenseKeys =
    loadedOrder?.status === OrderStatus.REVISION_REQUIRED
      ? getRevisionExpenseReviewKeys(loadedOrder.expenses as unknown as OrderExpenseLine[])
      : undefined;

  const workflowSteps = ORDER_WORKFLOW_STEP_KEYS.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));

  return (
    <Box>
      <DetailPageHeader
        title={
          isEdit
            ? t("standby.form.editTitle" as TranslationKeys)
            : t("standby.form.createTitle" as TranslationKeys)
        }
        subtitle={
          isEdit ? loadedOrder?.readableId : t("standby.form.newSubtitle" as TranslationKeys)
        }
        progressSlot={
          <StatusWorkflowStepper
            steps={workflowSteps}
            activeStepKey={getOrderWorkflowStepKey(loadedOrder?.status ?? OrderStatus.OPEN)}
          />
        }
        actions={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => navigate(isEdit && id ? `/orders/standby/${id}` : "/orders")}
              disabled={submitting}
              data-testid={TestId.STANDBY.CANCEL}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              {t("Cancel" as TranslationKeys)}
            </Button>
            <Button
              type="submit"
              form={STANDBY_ORDER_FORM_ID}
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              disabled={submitting}
              data-testid={TestId.STANDBY.SAVE}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              {t("Save" as TranslationKeys)}
            </Button>
          </Box>
        }
      />
      <StandbyOrderForm
        initialValues={initialValues}
        teams={memberTeams}
        cities={cities}
        trucks={trucks}
        users={users}
        isEdit={isEdit}
        canSelectTeam={canSelectTeam}
        loading={submitting}
        error={error}
        meta={formMeta}
        hideActions
        revisionExpenseKeys={revisionExpenseKeys}
        additionalExpenseIds={additionalExpenseIds}
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit && id ? `/orders/standby/${id}` : "/orders")}
      />
    </Box>
  );
};

export default StandbyOrderFormPage;
